import type { OfficeUserItem, RoleItem, UserList } from './setup-types.js';

/**
 * Settings ▸ Users'ın düğmeleri yönetici ve kendi satırını ayırır (ajan kararı, ADR 0151 §3).
 * Kural yazma yolundadır (ADR 0042 Ek U39, ADR 0150; `packages/db/src/office-users.ts`): yönetici
 * hesabı, yönetici rolü değen satır, kişinin kendi rolleri ve gemi atamaları ve gemi kapsamlı
 * yönetici şirket rolü taşıyan kişinin gemi atamaları yalnız yöneticiden (`settings.edit` filo);
 * kişi kendini devre dışı bırakamaz (`LAST_ADMIN` self). Ekran sunucunun
 * reddedeceği işlemi göstermez; yazma yolunun iki sürümünden (ADR 0150 öncesi ve sonrası) de sıkı
 * tarafta kalır.
 */

/** İşlemi yapan (`GET /api/office/users`). */
export interface UserActor {
  id: string;
  /** Şirket yöneticisi mi (`settings.edit` filo; `grantsMatrixAdmin`). */
  admin: boolean;
  /** Yönetici rolleri: `settings` modülünde yazma veren roller (ADR 0150 §1 `isAdminRole`). */
  adminRoleIds: readonly string[];
  /**
   * Yönetici yetkisi gemi atamalarıyla genişleyen roller: `settings` yazması gemi ya da "kendi"
   * kapsamlı (ADR 0150 §1 `adminScopeFollowsVessels`).
   */
  adminScopeRoleIds: readonly string[];
}

/** Eski sunucu işlemi yapanı göndermezse `null`: ekran kısıt koymaz, sunucu yine denetler. */
export function userActorOf(
  list: Partial<Pick<UserList, 'actorId' | 'actorIsAdmin' | 'adminRoleIds' | 'adminScopeRoleIds'>>,
): UserActor | null {
  if (list.actorId === undefined || list.actorIsAdmin === undefined) return null;
  return {
    id: list.actorId,
    admin: list.actorIsAdmin,
    adminRoleIds: list.adminRoleIds ?? [],
    adminScopeRoleIds: list.adminScopeRoleIds ?? [],
  };
}

/**
 * "New" (kişi açma): `users.create` ve `users.edit` (ADR 0150 Ek). Yazma yolu kişiyi pasif açar,
 * dizine yazar, sonra kişi satırını güncelleyerek etkinleştirir; `users.edit`'i olmayana düğme
 * gösterilmez (sunucu ilk yazmadan önce reddeder).
 */
export function newUserAllowed(list: Pick<UserList, 'canCreate' | 'canEdit'>): boolean {
  return list.canCreate && list.canEdit;
}

/** Bir satırda işlemi yapanın sunucuda geçecek işleri (`users.*` yetkisinin üstüne). */
export interface UserRowLimits {
  /** Giriş e-postası, parola ve TOTP sıfırlama, etkinleştirme: yönetici hesabında yönetici. */
  account: boolean;
  /** Devre dışı bırakma: hesap işlemi şartı ve kişinin kendisi değil. */
  disable: boolean;
  /** Şirket rollerini kaydetme: yönetici rolü değen satır ve kişinin kendi rolleri yöneticiden. */
  roles: boolean;
  /**
   * Gemi atamalarını kaydetme: kişinin kendi ataması ve gemi kapsamlı yönetici şirket rolü taşıyan
   * kişinin listesi yöneticiden (`adminScopeLocked`). Yönetici rolü taşıyan satır ayrıca
   * kilitlidir (`adminRowLocked`).
   */
  assignments: boolean;
}

export const NO_USER_LIMITS: UserRowLimits = {
  account: true,
  disable: true,
  roles: true,
  assignments: true,
};

/**
 * Yönetici hesabı (ADR 0150 §3 `isAdminAccount`; U39): bugün ya da ileride geçerli yönetici rolü
 * taşıyan şirket rolü ya da gemi ataması (bitişi yok ya da `today` ve sonrası). Yarın yönetici
 * olacak kişinin hesabı bugün de yalnız yöneticiye açıktır; bitmiş rol sayılmaz. `admin` (bugünkü
 * şirket rolü; eski sunucu `adminRoleIds` göndermezse tek bilgi) de sayılır.
 */
export function adminAccountOf(
  user: Pick<OfficeUserItem, 'admin' | 'companyRoles' | 'vesselAssignments'>,
  actor: Pick<UserActor, 'adminRoleIds'>,
  today: string,
): boolean {
  return (
    user.admin ||
    [...user.companyRoles, ...user.vesselAssignments].some(
      (row) =>
        actor.adminRoleIds.includes(row.roleId) && (row.validTo === null || row.validTo >= today),
    )
  );
}

/**
 * Gemi kapsamlı yönetici şirket rolü (ADR 0150 §1 `assertAdminScopeChange`): kişinin bugün ya da
 * ileride geçerli şirket rollerinden biri `settings` yazmasını gemi ya da "kendi" kapsamıyla
 * veriyorsa yönetici yetkisi atandığı gemilerle genişler; gemi atama listesini (Settings ▸ Users,
 * Sign On / Off) yalnız yönetici değiştirir. Bitmiş rol ve gemi ataması olarak taşınan rol
 * sayılmaz (yazma yolu yalnız şirket rollerine bakar; atama satırının kendisi `adminRowLocked`).
 * Eski sunucu `adminScopeRoleIds` göndermezse kısıt yok.
 */
export function adminScopeLocked(
  user: Pick<OfficeUserItem, 'companyRoles'>,
  actor: UserActor | null,
  today: string,
): boolean {
  return (
    actor !== null &&
    !actor.admin &&
    user.companyRoles.some(
      (row) =>
        actor.adminScopeRoleIds.includes(row.roleId) &&
        (row.validTo === null || row.validTo >= today),
    )
  );
}

/**
 * Hesap işlemlerinin sınırı (U39; ADR 0150 §3, §5; ADR 0151 §3): yönetici hesabında giriş
 * e-postası, parola ve TOTP sıfırlama, etkinleştirme, devre dışı bırakma ve hesap açma yalnız
 * yöneticiden (`accountAction`, `changeOfficeUserEmail`, `createLoginForPerson` →
 * `isAdminAccount`); kişi kendini devre dışı bırakamaz (`LAST_ADMIN` self). Settings ▸ Users ve
 * Crew ▸ Login Accounts aynı yargıyı kullanır. İşlemi yapan bilinmiyorsa (eski sunucu) kısıt yok.
 */
export function accountLimits(
  person: { id: string; adminAccount: boolean },
  actor: Pick<UserActor, 'id' | 'admin'> | null,
): Pick<UserRowLimits, 'account' | 'disable'> {
  if (actor === null) return { account: true, disable: true };
  const account = actor.admin || !person.adminAccount;
  return { account, disable: account && person.id !== actor.id };
}

/** `today`: ekranın günü (UTC `YYYY-MM-DD`; bulutun günüyle aynı). */
export function userRowLimits(
  user: Pick<OfficeUserItem, 'id' | 'admin' | 'companyRoles' | 'vesselAssignments'>,
  actor: UserActor | null,
  today: string,
): UserRowLimits {
  if (actor === null) return NO_USER_LIMITS;
  const self = user.id === actor.id;
  const { account, disable } = accountLimits(
    { id: user.id, adminAccount: adminAccountOf(user, actor, today) },
    actor,
  );
  // Sunucu şirket rollerinin önceki ve sonraki bütün satırlarına bakar (geçmiş ya da gelecek
  // yönetici rolü; ADR 0150 §1)
  const touchesAdmin =
    user.admin || user.companyRoles.some((r) => actor.adminRoleIds.includes(r.roleId));
  return {
    account,
    disable,
    roles: actor.admin || (!self && !touchesAdmin),
    assignments: actor.admin || (!self && !adminScopeLocked(user, actor, today)),
  };
}

/**
 * Gemi ataması satırı yönetici olmayana salt okunur mu (ADR 0150 §1; U39): yönetici rolünü
 * taşıyan satırı (geçmiş ve gelecek dahil) değiştirmek ya da kaldırmak yalnız yöneticiden.
 * Sunucu eklenen, değişen ya da kaldırılan satırın eski ve yeni rolüne bakar; değişmeyen satır
 * kayıtta kalır. Yeni satırda yönetici rolü zaten sunulmaz (`offeredRoles`).
 */
export function adminRowLocked(row: { roleId: string | null }, actor: UserActor | null): boolean {
  return (
    actor !== null && !actor.admin && row.roleId !== null && actor.adminRoleIds.includes(row.roleId)
  );
}

/**
 * Çekmecedeki gemi atamaları bölümü (ADR 0151 §3): yönetici olmayanın kendi satırı salt okunur
 * ve nedeni yazılı (`own`; kartlı kişide de, çünkü Sign On / Off'ta da kendi satırında işlem
 * yok); gemi kapsamlı yönetici şirket rolü taşıyan kişinin listesi de öyle (`adminScope`; Sign On
 * / Off'ta da işlem yok); kartlı kişinin ataması Sign On / Off'tan yazılır (`signOnOff`; ADR 0131
 * Ek 2); değilse düzenlenir (`edit`). `userRowLimits(...).assignments` ilk ikisinde `false`.
 */
export function assignmentsSection(
  user: Pick<OfficeUserItem, 'id' | 'crewCard' | 'companyRoles'>,
  actor: UserActor | null,
  today: string,
): 'own' | 'adminScope' | 'signOnOff' | 'edit' {
  if (actor !== null && !actor.admin && user.id === actor.id) return 'own';
  if (adminScopeLocked(user, actor, today)) return 'adminScope';
  return user.crewCard ? 'signOnOff' : 'edit';
}

export type UserAction =
  | 'edit'
  | 'changeEmail'
  | 'resetPassword'
  | 'resetTotp'
  | 'disable'
  | 'enable';

/**
 * Satır menüsünün işlemleri: düzenleme ve giriş e-postası `users.edit` (ADR 0145), hesap işlemleri
 * `users.void` (ADR 0042 §23) ve `users.edit` (kişi satırını yazarlar; tek kural için TOTP
 * sıfırlama da; ADR 0151 Ek). Yönetici hesabında yönetici şartı (U39) ve kendini devre dışı
 * bırakamama satırın sınırlarından gelir (`userRowLimits`; ADR 0151 §3); sunucu yine denetler.
 */
export function userMenuActions(
  user: Pick<OfficeUserItem, 'active'>,
  rights: { canEdit: boolean; canManage: boolean },
  limits: UserRowLimits = NO_USER_LIMITS,
): UserAction[] {
  const manage = rights.canManage && rights.canEdit && limits.account;
  return [
    ...(rights.canEdit ? (['edit'] as const) : []),
    ...(rights.canEdit && limits.account ? (['changeEmail'] as const) : []),
    ...(manage ? (['resetPassword', 'resetTotp'] as const) : []),
    ...(manage && !user.active ? (['enable'] as const) : []),
    ...(manage && user.active && limits.disable ? (['disable'] as const) : []),
  ];
}

/**
 * Satır menüsünde neden işlem eksik: yönetici hesabı (yalnız yönetici) ya da kişinin kendi
 * hesabı (devre dışı bırakamaz). Crew ▸ Login Accounts'taki `loginAccountNote` gibi: yalnız
 * işlemi yapanın yetkisinin verdiği bir işlem (`users.edit` ile giriş e-postası da) satırın
 * sınırı yüzünden gizlendiyse not var (ADR 0151 §3, Ek).
 */
export function userMenuNote(
  user: Pick<OfficeUserItem, 'active'>,
  rights: { canManage: boolean; canEdit: boolean },
  limits: UserRowLimits,
): 'adminAccount' | 'ownAccount' | null {
  if (userMenuActions(user, rights).length === userMenuActions(user, rights, limits).length) {
    return null;
  }
  return limits.account ? 'ownAccount' : 'adminAccount';
}

/**
 * Rol seçicisinin seçenekleri: yönetici olmayana yönetici rolü sunulmaz (sunucu `FORBIDDEN`
 * verir); kayıttaki seçili rol görünür kalır.
 */
export function offeredRoles<T extends Pick<RoleItem, 'id'>>(
  roles: readonly T[],
  actor: UserActor | null,
  selected: string | null,
): T[] {
  if (actor === null || actor.admin) return [...roles];
  return roles.filter((role) => role.id === selected || !actor.adminRoleIds.includes(role.id));
}
